import { Button } from '@amore/ui/components/button'
import { Card, CardTitle } from '@amore/ui/components/card'
import { Text } from '@amore/ui/components/typography'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, FileText, Plus, Wallet } from 'lucide-react'
import type { CrewWithSupplier } from '@/mock/photo'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { type Prompt, PromptList, taskPromptState, useAddPromptTask } from '@/patterns/prompts'
import { ScreenSection } from '@/patterns/screen'
import { useToast } from '@/shell/toaster'
import { byDueThenPriority } from '../tasks/model'
import { CompleteToggle, TaskTable, taskColumns } from '../tasks/task-table'

/** A pointer to the Pro fotografa tab: the one-page brief and how to share it. */
export function PhotoBriefCard({ hasPhotographer }: { hasPhotographer: boolean }) {
  return (
    <Card>
      <div className="flex items-center gap-2.5">
        <FileText className="size-4 text-action" aria-hidden="true" />
        <CardTitle as="h2">Podklady pro fotografa</CardTitle>
      </div>
      <Text tone="muted" className="mt-3">
        {hasPhotographer
          ? 'Jedna stránka se vším, co fotograf potřebuje: program, lidé, skupinové fotky a vaše přání. Odkaz je vždy aktuální.'
          : 'Až budete mít fotografa, pošlete mu jednu stránku se vším, co potřebuje vědět.'}
      </Text>
      <div className="mt-3 flex justify-end">
        <Link
          to="/photo/brief"
          className="inline-flex min-h-9 items-center gap-1.5 px-3 text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          Zobrazit podklady
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

export function PhotoPrompts({
  crew,
  onAddPhotographer,
}: {
  crew: CrewWithSupplier[]
  onAddPhotographer: () => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const addTask = useAddPromptTask()
  const { showToast } = useToast()
  if (!canEdit) return null

  const photographers = crew.filter((member) => member.role === 'photo' && member.supplier)

  const candidates: (Omit<Prompt, 'action' | 'state'> & {
    relevant: boolean
    taskTitle?: string
    pick?: string
  })[] = [
    {
      code: 'photo-photographer',
      title: 'Zatím nemáte fotografa',
      hint: 'Termíny oblíbených fotografů se plní rok dopředu. Vyberte z Mých dodavatelů, nebo z katalogu.',
      pick: 'Vybrat fotografa',
      relevant: photographers.length === 0,
    },
    {
      code: 'photo-engagement',
      title: 'Zásnubní nebo poobřadní focení?',
      hint: 'Mnoho fotografů ho má v balíčku. Oťukáte se spolu ještě před velkým dnem.',
      taskTitle: 'Domluvit zásnubní focení',
      relevant: photographers.length > 0,
    },
  ]

  return (
    <PromptList
      title="Co ještě chybí"
      prompts={candidates.flatMap(({ relevant, taskTitle, pick, ...prompt }) => {
        if (!relevant || data.hiddenPrompts.includes(prompt.code)) return []
        const state = taskTitle
          ? taskPromptState(
              data.tasks.filter((task) => task.context?.type === 'photo'),
              taskTitle,
            )
          : 'open'
        if (state === 'done') return []
        return [
          {
            ...prompt,
            state,
            action: taskTitle ? (
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => addTask(taskTitle, { labels: [], context: { type: 'photo' } })}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Přidat úkol
              </Button>
            ) : (
              <Button appearance="outline" tone="neutral" size="sm" onClick={onAddPhotographer}>
                {pick}
              </Button>
            ),
          },
        ]
      })}
      onHide={(code) => {
        update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
        showToast({
          title: 'Doporučení skryto',
          action: {
            label: 'Vrátit zpět',
            altText: 'Znovu zobrazit doporučení k focení',
            onClick: () =>
              update((current) => ({
                ...current,
                hiddenPrompts: current.hiddenPrompts.filter((item) => item !== code),
              })),
          },
        })
      }}
    />
  )
}

export function PhotoTasks() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const tasks = data.tasks
    .filter((task) => task.context?.type === 'photo')
    .sort((left, right) => Number(left.done) - Number(right.done) || byDueThenPriority(left, right))
  return (
    <ScreenSection
      title="Co je potřeba zařídit"
      description="Úkoly k focení a videu jsou součástí vašeho společného svatebního plánu."
      action={
        <Link
          to="/tasks"
          className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          Otevřít společné úkoly
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      }
    >
      <TaskTable
        tasks={tasks}
        grouped
        columns={taskColumns(data)}
        onOpen={(task) => void navigate({ to: `/tasks/${task.id}` })}
        rowAction={(task) => (
          <CompleteToggle
            task={task}
            canEdit={canEdit}
            onChange={(done) =>
              update((current) => ({
                ...current,
                tasks: replaceById(current.tasks, task.id, { done }),
              }))
            }
          />
        )}
        empty="Zatím žádné úkoly k focení. Až nějaký přidáte, najdete ho tady i ve společných úkolech."
      />
    </ScreenSection>
  )
}

export function PhotoBudgetNote() {
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
      <Wallet aria-hidden="true" className="size-3.5 shrink-0" />
      Ceny, zálohy a platby za fotografa a kameramana máte v rozpočtu.
      <Link
        to="/budget"
        className="font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
      >
        Otevřít rozpočet
      </Link>
    </p>
  )
}
